import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Visibility, CheckCircle, DeleteOutline } from "@mui/icons-material";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../services/firebase";

interface TopicRequest {
  id: string;
  content: string;
  timestamp: { _seconds: number };
  createdAt: { _seconds: number };
  status: "pending" | "read" | "resolved";
  platform: string | null;
  osVersion: string | null;
  deviceModel: string | null;
  appVersion: string | null;
}

const TopicRequestList: React.FC = () => {
  const [requests, setRequests] = useState<TopicRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<TopicRequest | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TopicRequest | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "customerTopicRequests"),
        orderBy("createdAt", "desc"),
        limit(100)
      );
      const snapshot = await getDocs(q);
      const requestList = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const createdAt = data.createdAt as Timestamp | null;
        const timestamp = data.timestamp as Timestamp | null;
        return {
          id: docSnap.id,
          content: data.content,
          status: data.status || "pending",
          createdAt: createdAt
            ? { _seconds: createdAt.seconds }
            : { _seconds: Math.floor(Date.now() / 1000) },
          timestamp: timestamp
            ? { _seconds: timestamp.seconds }
            : { _seconds: Math.floor(Date.now() / 1000) },
          platform: data.platform || null,
          osVersion: data.osVersion || null,
          deviceModel: data.deviceModel || null,
          appVersion: data.appVersion || null,
        } as TopicRequest;
      });
      setRequests(requestList);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleOpenDialog = (request: TopicRequest) => {
    setSelectedRequest(request);
    setDialogOpen(true);

    if (request.status === "pending") {
      updateRequestStatus(request.id, "read");
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRequest(null);
  };

  const updateRequestStatus = async (requestId: string, status: "read" | "resolved") => {
    try {
      const docRef = doc(db, "customerTopicRequests", requestId);
      await updateDoc(docRef, {
        status,
        updatedAt: serverTimestamp(),
      });

      setRequests((prev) =>
        prev.map((request) => (request.id === requestId ? { ...request, status } : request))
      );

      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status });
      }
    } catch (err) {
      console.error("Error updating topic request status:", err);
      alert("ステータスの更新に失敗しました");
    }
  };

  const handleMarkAsResolved = async () => {
    if (selectedRequest) {
      await updateRequestStatus(selectedRequest.id, "resolved");
      handleCloseDialog();
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteDoc(doc(db, "customerTopicRequests", deleteTarget.id));
      setRequests((prev) => prev.filter((request) => request.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Error deleting topic request:", err);
      alert("削除に失敗しました");
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "未読";
      case "read":
        return "既読";
      case "resolved":
        return "対応済み";
      default:
        return status;
    }
  };

  const getStatusColor = (
    status: string
  ): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case "pending":
        return "error";
      case "read":
        return "warning";
      case "resolved":
        return "success";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            お題リクエスト
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            ユーザーからのお題リクエストの一覧です
          </Typography>

          <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ステータス</TableCell>
                  <TableCell>投稿日時</TableCell>
                  <TableCell>端末</TableCell>
                  <TableCell>内容（プレビュー）</TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>
                    操作
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      リクエストはありません
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(request.status)}
                          color={getStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(request.createdAt._seconds * 1000).toLocaleString("ja-JP")}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {request.deviceModel || "-"}
                        </Typography>
                        {request.platform && (
                          <Typography variant="caption" color="text.secondary">
                            {request.platform} {request.osVersion || ""}
                            {request.appVersion ? ` / v${request.appVersion}` : ""}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {request.content.length > 50
                          ? `${request.content.substring(0, 50)}...`
                          : request.content}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenDialog(request)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setDeleteTarget(request)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 詳細ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          お題リクエスト詳細
          {selectedRequest && (
            <Chip
              label={getStatusLabel(selectedRequest.status)}
              color={getStatusColor(selectedRequest.status)}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                投稿日時
              </Typography>
              <Typography variant="body1" paragraph>
                {new Date(selectedRequest.createdAt._seconds * 1000).toLocaleString("ja-JP")}
              </Typography>

              {selectedRequest.platform && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    端末情報
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedRequest.deviceModel || "不明"} / {selectedRequest.platform}{" "}
                    {selectedRequest.osVersion || ""} / アプリ v
                    {selectedRequest.appVersion || "不明"}
                  </Typography>
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                内容
              </Typography>
              <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
                  {selectedRequest.content}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedRequest && selectedRequest.status !== "resolved" && (
            <Button
              startIcon={<CheckCircle />}
              variant="contained"
              color="success"
              onClick={handleMarkAsResolved}
            >
              対応済みにする
            </Button>
          )}
          <Button onClick={handleCloseDialog}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>お題リクエストを削除しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            この操作は取り消せません。イタズラなど不要な投稿を完全に削除します。
          </Typography>
          {deleteTarget && (
            <Paper sx={{ p: 2, bgcolor: "grey.50", mt: 2 }}>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                {deleteTarget.content.length > 200
                  ? `${deleteTarget.content.substring(0, 200)}...`
                  : deleteTarget.content}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TopicRequestList;
